import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  ActionIcon, Box, Button, Group, Modal, Stack, Text,
} from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { duplicateAtIndex, moveToInsertIndex } from '../../utils/listOrder';

/** Bande de défilement automatique en haut/bas du conteneur pendant un glissement. */
const AUTOSCROLL_EDGE = 48;
const AUTOSCROLL_SPEED = 12;

/** Premier ancêtre réellement scrollable — la sidebar Puck, en pratique. */
function findScrollParent(node) {
  let el = node?.parentElement;
  while (el) {
    const { overflowY } = window.getComputedStyle(el);
    if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

/**
 * Liste d'items repliables pour un champ Puck `custom` (liens du footer,
 * étapes de la frise…). Le champ `array` natif de Puck n'expose aucun hook
 * pour ouvrir l'item qu'on vient d'ajouter : en gérant la liste et l'état
 * ouvert/fermé ici, le nouvel item s'ouvre directement.
 *
 * Chaque item se duplique depuis son en-tête (la copie se place juste après
 * l'original et s'ouvre) — refaire à la main une slide ou une étape déjà
 * réglée était la manœuvre la plus pénible de ces listes.
 *
 * Réordonnancement à la souris (même pattern que la liste de pages de
 * `admin/WebTemplateEditor`) plutôt qu'en HTML5 drag-and-drop : pas de
 * collision avec les capteurs pointer de Puck. La position d'insertion est
 * recalculée en continu à partir des rectangles réels, ce qui la garde juste
 * même quand la sidebar défile toute seule sous le curseur.
 *
 * @param {object[]} value
 * @param {(next: object[]) => void} onChange
 * @param {(count: number) => object} createItem - item ajouté par le bouton
 * @param {(item: object, index: number) => string} itemTitle - résumé replié
 * @param {(item: object, update: (patch: object) => void, index: number) => React.ReactNode} renderItem
 * @param {string} addLabel
 * @param {(item: object, index: number) => React.ReactNode} [renderItemActions] -
 *   actions supplémentaires dans l'en-tête, avant la corbeille (ex. l'œil
 *   d'aperçu d'une slide de carrousel). Le rendu doit stopper la propagation
 *   du clic, sinon l'item se replie.
 * @param {number} [minItems] - plancher : la corbeille se désactive dessus.
 * @param {(item: object, index: number) => string|null} [confirmRemove] -
 *   message de confirmation, ou `null` pour supprimer sans rien demander. Sert
 *   à ce dont la suppression détruit plus que la ligne visible (le contenu
 *   d'un onglet, par exemple, est invisible au moment du clic).
 */
export default function CollapsibleListField({
  value,
  onChange,
  createItem,
  itemTitle,
  renderItem,
  addLabel,
  renderItemActions,
  minItems = 0,
  confirmRemove,
  reveal,
}) {
  // Référence stable : l'effet de glissement ne doit pas se réabonner à chaque rendu.
  const items = useMemo(() => value || [], [value]);
  const [openIndex, setOpenIndex] = useState(null);
  useEffect(() => { if (reveal) setOpenIndex(reveal.index); }, [reveal]);
  const [dragIndex, setDragIndex] = useState(null);
  const [insertIndex, setInsertIndex] = useState(null);
  /** Suppression retenue en attendant confirmation : `{ index, message }`. */
  const [pendingRemove, setPendingRemove] = useState(null);

  const rootRef = useRef(null);
  const itemRefs = useRef([]);
  const scrollParentRef = useRef(null);
  const pointerYRef = useRef(null);
  const rafRef = useRef(null);
  // La position d'insertion vit aussi en ref : la boucle rAF la relit sans
  // que l'effet se réabonne à chaque frame.
  const insertIndexRef = useRef(null);

  const update = (index, patch) => {
    onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const add = () => {
    const next = [...items, createItem(items.length)];
    onChange(next);
    setOpenIndex(next.length - 1);
  };

  const applyRemove = (index) => {
    onChange(items.filter((_, i) => i !== index));
    setOpenIndex(null);
  };

  const remove = (index) => {
    const message = confirmRemove?.(items[index], index);
    if (message) {
      setPendingRemove({ index, message });
      return;
    }
    applyRemove(index);
  };

  // Comme l'ajout : la copie s'ouvre, puisqu'on duplique pour retoucher.
  const duplicate = (index) => {
    const { list, insertedIndex } = duplicateAtIndex(items, index);
    if (insertedIndex === -1) return;
    onChange(list);
    setOpenIndex(insertedIndex);
  };

  /** Position d'insertion = 1er item dont le pointeur dépasse le milieu. */
  const insertIndexAt = useCallback((y) => {
    const rects = itemRefs.current
      .slice(0, items.length)
      .filter(Boolean)
      .map((el) => el.getBoundingClientRect());
    const found = rects.findIndex((r) => y < r.top + r.height / 2);
    return found === -1 ? rects.length : found;
  }, [items.length]);

  useEffect(() => {
    if (dragIndex == null) return undefined;

    const previousUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';

    const onMouseMove = (e) => { pointerYRef.current = e.clientY; };

    // Boucle rAF : suit le pointeur ET fait défiler la sidebar quand on
    // s'approche d'un bord — sans ça, réordonner une longue liste oblige à
    // relâcher, scroller, recommencer.
    const step = () => {
      const y = pointerYRef.current;
      if (y != null) {
        const container = scrollParentRef.current;
        if (container) {
          const rect = container.getBoundingClientRect();
          if (y < rect.top + AUTOSCROLL_EDGE) container.scrollTop -= AUTOSCROLL_SPEED;
          else if (y > rect.bottom - AUTOSCROLL_EDGE) container.scrollTop += AUTOSCROLL_SPEED;
        }
        const next = insertIndexAt(y);
        if (next !== insertIndexRef.current) {
          insertIndexRef.current = next;
          setInsertIndex(next);
        }
      }
      rafRef.current = window.requestAnimationFrame(step);
    };
    rafRef.current = window.requestAnimationFrame(step);

    const onMouseUp = () => {
      if (insertIndexRef.current != null) {
        onChange(moveToInsertIndex(items, dragIndex, insertIndexRef.current));
        setOpenIndex(null);
      }
      setDragIndex(null);
      setInsertIndex(null);
      insertIndexRef.current = null;
      pointerYRef.current = null;
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.cancelAnimationFrame(rafRef.current);
      document.body.style.userSelect = previousUserSelect;
    };
  }, [dragIndex, items, onChange, insertIndexAt]);

  const startDrag = (index) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    scrollParentRef.current = findScrollParent(rootRef.current);
    pointerYRef.current = e.clientY;
    insertIndexRef.current = null;
    setDragIndex(index);
    setInsertIndex(null);
  };

  const isDragging = dragIndex != null;

  /** Trait d'insertion — toujours rendu (hauteur figée) : aucun saut de mise en page. */
  const dropLine = (index) => (
    <Box
      key={`drop-${index}`}
      style={{
        height: 2,
        borderRadius: 2,
        backgroundColor: isDragging && insertIndex === index
          ? 'var(--mantine-color-editor-6)'
          : 'transparent',
      }}
    />
  );

  return (
    <Stack gap={4} ref={rootRef}>
      {items.map((item, index) => {
        const isOpen = openIndex === index;
        const title = itemTitle(item, index);
        const isDragged = dragIndex === index;
        return (
          <React.Fragment key={index}>
            {dropLine(index)}
            <Box
              ref={(el) => { itemRefs.current[index] = el; }}
              style={{
                border: `1px solid ${isDragged
                  ? 'var(--mantine-color-editor-5)'
                  : 'var(--mantine-color-gray-4)'}`,
                borderRadius: 6,
                backgroundColor: 'var(--mantine-color-body)',
                boxShadow: isDragged ? 'var(--mantine-shadow-md)' : undefined,
                opacity: isDragged ? 0.85 : 1,
              }}
            >
              <Group
                justify="space-between"
                wrap="nowrap"
                p={6}
                gap={4}
                style={{ cursor: 'pointer' }}
                onClick={() => setOpenIndex(isOpen ? null : index)}
              >
                <Group gap={4} wrap="nowrap" style={{ minWidth: 0 }}>
                  <Box
                    aria-label={`Déplacer ${title}`}
                    onMouseDown={startDrag(index)}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '2px 0',
                      color: 'var(--mantine-color-gray-6)',
                      cursor: isDragging ? 'grabbing' : 'grab',
                    }}
                  >
                    <FontAwesomeIcon
                      icon={icon({ name: 'grip-vertical', style: 'solid' })}
                      style={{ fontSize: 14 }}
                    />
                  </Box>
                  <Text size="sm" fw={500} truncate>{title}</Text>
                </Group>
                <Group gap={2} wrap="nowrap">
                  {renderItemActions ? renderItemActions(item, index) : null}
                  <ActionIcon
                    variant="subtle"
                    color="editor"
                    size="sm"
                    aria-label={`Dupliquer ${title}`}
                    title="Dupliquer"
                    onClick={(e) => { e.stopPropagation(); duplicate(index); }}
                  >
                    <FontAwesomeIcon
                      icon={icon({ name: 'copy', style: 'light' })}
                      style={{ fontSize: 14 }}
                    />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="sm"
                    aria-label={`Supprimer ${title}`}
                    title={items.length <= minItems
                      ? 'Il doit en rester au moins un'
                      : 'Supprimer'}
                    disabled={items.length <= minItems}
                    onClick={(e) => { e.stopPropagation(); remove(index); }}
                  >
                    <FontAwesomeIcon
                      icon={icon({ name: 'trash', style: 'light' })}
                      style={{ fontSize: 14 }}
                    />
                  </ActionIcon>
                  {isOpen
                    ? <FontAwesomeIcon icon={icon({ name: 'chevron-up', style: 'solid' })} style={{ fontSize: 14 }} />
                    : <FontAwesomeIcon icon={icon({ name: 'chevron-down', style: 'solid' })} style={{ fontSize: 14 }} />}
                </Group>
              </Group>
              {isOpen && (
                <Stack gap={6} p={6} pt={0}>
                  {renderItem(item, (patch) => update(index, patch), index)}
                </Stack>
              )}
            </Box>
          </React.Fragment>
        );
      })}
      {dropLine(items.length)}
      {/* Couleur explicite : l'éditeur de template applique le thème du site à
          sa sidebar, celui de page non — sans ça le même bouton change de
          teinte d'un écran à l'autre. */}
      <Button
        type="button"
        size="xs"
        variant="light"
        color="editor"
        mt={2}
        leftSection={<FontAwesomeIcon icon={icon({ name: 'plus', style: 'light' })} style={{ fontSize: 14 }} />}
        onClick={add}
      >
        {addLabel}
      </Button>

      {/* Le projet n'a pas @mantine/modals : Modal local, comme la création de
          page statique de admin/WebTemplateEditor. */}
      <Modal
        opened={pendingRemove != null}
        onClose={() => setPendingRemove(null)}
        title="Confirmer la suppression"
        centered
        zIndex={1000}
      >
        <Text size="sm">{pendingRemove?.message}</Text>
        <Group justify="flex-end" mt="md">
          <Button size="xs" variant="default" onClick={() => setPendingRemove(null)}>
            Annuler
          </Button>
          <Button
            size="xs"
            color="red"
            onClick={() => {
              applyRemove(pendingRemove.index);
              setPendingRemove(null);
            }}
          >
            Supprimer
          </Button>
        </Group>
      </Modal>
    </Stack>
  );
}
